import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { t } from "i18next";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";

import ButtonTabs from "@components/ButtonTabs/ButtonTabs";
import OrganisationHeader from "@components/OrganisationHeader/OrganisationHeader";
import GoogleMap from "@components/shared/GoogleMap/GoogleMap";
import Loader from "@components/shared/Loader/Loader";
import Tabs from "@components/shared/Tabs/Tabs";
import { CONTACT_PERSON_TYPES } from "@constants/contactPerson";
import { tabItems } from "@constants/organisation";
import { routeEnum, supportEnum } from "@enums/route.enum";
import { ResourceInfoType } from "@models/components/units.model";
import { SingleSlot } from "@models/services/booking";
import { getLocationType, locationType } from "@models/services/organisation";
import { userProfile } from "@models/services/user";
import { ContactDetailsType } from "@models/shared/shared.model";
import { LocalizationObjectType } from "@models/utils/localisation.models";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import bookingSlice from "@slices/booking.slice";
import organisationSlice from "@slices/organisation.slice";
import { RootState } from "@store/index";

const OrganisationInfoPageComponent: React.FC = () => {
  const params = useParams();
  const { accessToken } = useSelector((State: RootState) => State.local);
  const currentUrl = window.location.href;

  const [locationDetails, setLocationDetails] = useState<getLocationType>();
  const [activeLocation, setActiveLocation] = useState<locationType>(
    {} as locationType
  );
  const [userProfile, setUserProfile] = useState<userProfile>();
  const [contactPersonDetails, setContactPersonDetails] = useState(
    {} as ContactDetailsType
  );
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { locationId } = useSelector((State: RootState) => State.organisation);

  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const locationIdFromParams = queryParameters.get("locationId")
    ? queryParameters.get("locationId")
    : "0";

  const paymentRef: RefObject<HTMLDivElement | null> = useRef(null);

  const pricingRef: RefObject<HTMLDivElement | null> = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [locationDetails]);

  useEffect(() => {
    getLocation();
  }, []);

  useEffect(() => {
    if (accessToken !== "") getUserProfile();
  }, [accessToken]);

  useEffect(() => {
    getContactPerson(activeLocation?.id);
  }, [activeLocation?.id]);

  const getContactPerson = (locationId: string) => {
    if (locationId) getContactPersonCallback(locationId);
    else setContactPersonDetails({} as ContactDetailsType);
  };

  const getContactPersonCallback = useCallback(async (locationId: string) => {
    const data = await API_SERVICES.CONTACT_PERSON.GET_CONTACT_PERSON(
      locationId,
      CONTACT_PERSON_TYPES.LOCATION
    );
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      setContactPersonDetails(responseData.data);
    } else setContactPersonDetails({} as ContactDetailsType);
  }, []);

  const getLocation = useCallback(async () => {
    setIsLoading(true);
    const data = await API_SERVICES.ORGANISATION.GET_LOCATION(
      params.clubId || ""
    );
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      setLocationDetails(responseData.data);
      setInitialLocation(responseData?.data);
    } else {
      navigate(routeEnum.Home);
    }
    setIsLoading(false);
  }, []);

  const getUserProfile = useCallback(async () => {
    const data = await API_SERVICES.USER.PROFILE();
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      setUserProfile(responseData.data);
    }
  }, []);

  const handleLocationChange = (tab: locationType) => {
    setActiveLocation(tab);
    navigate(`/organisation-info/${params?.clubId}/booking`);
    dispatch(organisationSlice.actions.changeResourceId({ id: "0" }));
    dispatch(bookingSlice.actions.changeSelectedTime(""));
    dispatch(bookingSlice.actions.changeSelectedSlot({} as SingleSlot));
    dispatch(bookingSlice.actions.setGuests([]));
  };

  const setInitialLocation = (locationDetails: getLocationType) => {
    const InitialLocationId = locationIdFromParams || locationId;
    let setDefaultLocation = true;

    if (InitialLocationId !== "0") {
      locationDetails?.locations?.filter(obj => {
        if (obj.id === InitialLocationId) {
          setActiveLocation(obj);
          setDefaultLocation = false;
        }
      });
    }

    if (setDefaultLocation && locationDetails?.locations?.length) {
      setActiveLocation(locationDetails?.locations[0]);
    }
  };

  return (
    <div data-testid="organisationInfoPage-test">
      <Loader isLoading={isLoading} message={t("fetchingClubDetails")} />
      <OrganisationHeader
        name={locationDetails?.title || ({} as LocalizationObjectType)}
        logo={locationDetails?.mobileLogo}
        headerImages={activeLocation?.headerImage}
      />

      {locationDetails?.locations && locationDetails?.locations.length > 1 && (
        <div
          className="fg-overview-location-wrap"
          data-testid="organisation-buttons"
        >
          <div className="container">
            <div className="fg-location-block">
              <ButtonTabs
                tabs={locationDetails?.locations}
                selectedTab={activeLocation?.id}
                handleTabChange={
                  handleLocationChange as (
                    tab: locationType | ResourceInfoType
                  ) => void
                }
              />
            </div>
          </div>
        </div>
      )}

      <div className="fg-tab-wrap" data-testid="organisation-info-tabs">
        <Tabs labels={tabItems(locationDetails || ({} as getLocationType))} />
        <Outlet
          context={[
            locationDetails,
            activeLocation,
            userProfile,
            contactPersonDetails,
            paymentRef,
            pricingRef,
          ]}
        />
      </div>
      {activeLocation?.map?.latitude &&
        !currentUrl?.includes(supportEnum.Booking) && (
          <GoogleMap coordinates={activeLocation?.map} />
        )}
    </div>
  );
};

const OrganisationInfoPage = withTranslation()(OrganisationInfoPageComponent);

export default OrganisationInfoPage;
